import { useMatchRoute } from '@aibees/shared/hooks';
import { useSearchParams } from '@aibees/shared/router';
import { useMemo } from 'react';
import { ROUTES as R } from '../router';
import {
  ElephantPresetType,
  SearchBrowseParams,
  SearchMode,
  SearchTable,
  SearchType,
} from '../types';

/** URL param key for similarity/semantic search value. Use instead of hardcoding 'value'. */
export const SEARCH_PARAM_VALUE_KEY = 'value' as keyof SearchBrowseParams;

type ReturnType = {
  isSimilarity: boolean;
  isSemantic: boolean;
  mode?: SearchMode;
  type?: SearchType;
  table?: SearchTable;
  searchKey: keyof SearchBrowseParams;
  presetType?: ElephantPresetType;
};

/**
 * @deprecated Use granular hooks instead: useSearchTypeFromRoute, useSearchTableFromRoute,
 * useSearchRouteInfo, useSearchSimilarityFromRoute, useSearchModeFromRoute, useSearchSemanticFromRoute,
 * and SEARCH_PARAM_VALUE_KEY for the search param key. This hook re-renders on every URL change.
 */
export function useObtainSearchTypeFromRoute(): ReturnType {
  const params = useSearchParams();

  const isSimilarity = !!params.value;
  const isSemantic =
    params.type?.toLocaleLowerCase() ===
    SearchMode.Semantic.toLocaleLowerCase();
  const matchCompanySearch = useMatchRoute(R.searchCompany);
  const matchPeopleSearch = useMatchRoute(R.searchPeople);
  const matchCompanyEmployees = useMatchRoute(R.companyEmployees);

  const mode = useMemo(() => {
    if (isSemantic) return SearchMode.Semantic;
    if (isSimilarity) return SearchMode.Similarity;
    return undefined;
  }, [isSemantic, isSimilarity]);

  const type = useMemo(() => {
    if (matchCompanySearch) return SearchType.Company;
    if (matchPeopleSearch || matchCompanyEmployees) return SearchType.People;
    return undefined;
  }, [matchCompanySearch, matchPeopleSearch, matchCompanyEmployees]);

  const table = useMemo(() => {
    if (matchCompanySearch) return SearchTable.Company;
    if (matchPeopleSearch) return SearchTable.People;
    if (matchCompanyEmployees) return SearchTable.Employees;
    return undefined;
  }, [matchCompanySearch, matchPeopleSearch, matchCompanyEmployees]);

  const presetType = useMemo(() => {
    if (matchCompanySearch) return ElephantPresetType.COMPANY_SAVED_SEARCH;
    if (matchPeopleSearch) return ElephantPresetType.PEOPLE_SAVED_SEARCH;
    return undefined;
  }, [matchCompanySearch, matchPeopleSearch]);

  return {
    isSimilarity,
    isSemantic,
    mode,
    type,
    table,
    presetType,
    searchKey: SEARCH_PARAM_VALUE_KEY,
  };
}

// ---------------------------------------------------------------------------
// Route-only hooks: do NOT call useSearchParams, so they won't re-render on
// filter / URL-param changes. Use these wherever you only need route-derived
// information (type, table, presetType).
// ---------------------------------------------------------------------------

function useRouteMatches() {
  const matchCompanySearch = useMatchRoute(R.searchCompany);
  const matchPeopleSearch = useMatchRoute(R.searchPeople);
  const matchCompanyEmployees = useMatchRoute(R.companyEmployees);
  return { matchCompanySearch, matchPeopleSearch, matchCompanyEmployees };
}

/** Returns only `table` (SearchTable) derived from route matching. */
export function useSearchTableFromRoute(): SearchTable | undefined {
  const { matchCompanySearch, matchPeopleSearch, matchCompanyEmployees } =
    useRouteMatches();

  return useMemo(() => {
    if (matchCompanySearch) return SearchTable.Company;
    if (matchPeopleSearch) return SearchTable.People;
    if (matchCompanyEmployees) return SearchTable.Employees;
    return undefined;
  }, [matchCompanySearch, matchPeopleSearch, matchCompanyEmployees]);
}

/** Returns only `type` (SearchType) derived from route matching. */
export function useSearchTypeFromRoute(): SearchType | undefined {
  const { matchCompanySearch, matchPeopleSearch, matchCompanyEmployees } =
    useRouteMatches();

  return useMemo(() => {
    if (matchCompanySearch) return SearchType.Company;
    if (matchPeopleSearch || matchCompanyEmployees) return SearchType.People;
    return undefined;
  }, [matchCompanySearch, matchPeopleSearch, matchCompanyEmployees]);
}

type SearchRouteInfo = {
  type?: SearchType;
  table?: SearchTable;
  presetType?: ElephantPresetType;
};

/** Returns `{ type, table, presetType }` — all derived from route matching. */
export function useSearchRouteInfo(): SearchRouteInfo {
  const { matchCompanySearch, matchPeopleSearch, matchCompanyEmployees } =
    useRouteMatches();

  const type = useMemo(() => {
    if (matchCompanySearch) return SearchType.Company;
    if (matchPeopleSearch || matchCompanyEmployees) return SearchType.People;
    return undefined;
  }, [matchCompanySearch, matchPeopleSearch, matchCompanyEmployees]);

  const table = useMemo(() => {
    if (matchCompanySearch) return SearchTable.Company;
    if (matchPeopleSearch) return SearchTable.People;
    if (matchCompanyEmployees) return SearchTable.Employees;
    return undefined;
  }, [matchCompanySearch, matchPeopleSearch, matchCompanyEmployees]);

  const presetType = useMemo(() => {
    if (matchCompanySearch) return ElephantPresetType.COMPANY_SAVED_SEARCH;
    if (matchPeopleSearch) return ElephantPresetType.PEOPLE_SAVED_SEARCH;
    return undefined;
  }, [matchCompanySearch, matchPeopleSearch]);

  return useMemo(
    () => ({ type, table, presetType }),
    [type, table, presetType]
  );
}

/** Returns only `isSimilarity` (from URL params). Use when you only need similarity flag. */
export function useSearchSimilarityFromRoute(): boolean {
  const params = useSearchParams();
  return !!params.value;
}

/** Returns only whether search mode is semantic (from URL params). */
export function useSearchSemanticFromRoute(): boolean {
  const params = useSearchParams();
  return (
    params.type?.toLocaleLowerCase() === SearchMode.Semantic.toLocaleLowerCase()
  );
}

/** Returns only `mode` (SearchMode) from URL params. Use with useSearchTypeFromRoute or useSearchRouteInfo when you need type + mode. */
export function useSearchModeFromRoute(): SearchMode | undefined {
  const params = useSearchParams();
  const isSimilarity = !!params.value;
  const isSemantic =
    params.type?.toLocaleLowerCase() ===
    SearchMode.Semantic.toLocaleLowerCase();
  return useMemo(() => {
    if (isSemantic) return SearchMode.Semantic;
    if (isSimilarity) return SearchMode.Similarity;
    return undefined;
  }, [isSemantic, isSimilarity]);
}
